import { useMemo } from 'react';

import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
} from '@sg/shared-fe/features/customization';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventEffect } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const useSortedModifications = (params: Params) => {
  const { actor } = params;

  const isPremiumDisplayTestEnabled = useFeatureFlag('MNU-9207-pdp-premium-top-display'); // prettier-ignore
  const { modificationGroups } = useCustomizationContext({ actor });

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const sortedModificationGroups = useMemo(() => {
    if (!modificationGroups) return;

    if (!isPremiumDisplayTestEnabled) {
      return modificationGroups;
    }

    const premiumGroups = modificationGroups.filter(checkIfPremiumGroup);
    const nonPremiumGroups = modificationGroups.filter(checkIfOtherGroup);

    return [...premiumGroups, ...nonPremiumGroups];
  }, [modificationGroups, isPremiumDisplayTestEnabled]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'customization.ab-test.cohort-assigned',
    skip: !isPremiumDisplayTestEnabled,
    payload: {
      test_name: 'premium-top-display',
      cohort: isPremiumDisplayTestEnabled ? 'test' : 'control',
    },
  });

  // ───────────────────────────────────────────────────────────────────────────

  return sortedModificationGroups;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PREMIUM_GROUP_NAME = 'premium';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfPremiumGroup(group: CustomizationMachineTypes.ExtendedModifierGroup) {
  return group.displayName?.toLowerCase().includes(PREMIUM_GROUP_NAME);
}

function checkIfOtherGroup(group: CustomizationMachineTypes.ExtendedModifierGroup) {
  return !checkIfPremiumGroup(group);
}

// ─── Type ────────────────────────────────────────────────────────────────────

type Params = {
  actor: CustomizationMachineActorRef;
};
